import { useCallback, useMemo } from 'react';
import type { Epic, StrategicEpic } from '../../../api/tasks-client/task-client.type';
import { useTranslation } from 'react-i18next';
import type { FilterGroup as BaseFilterGroup } from '../../../components/filters/filter-panel.type';
import { useStrategyStore } from '../../../store/strategy-store/strategy-store';
import {
  setEpicCompletionFilters,
  setEpicCreatedDateFilters,
  setEpicDueDateStateFilters,
  setEpicProjectFilters,
  setEpicTargetEndDateFilters,
} from '../../../store/strategy-store/strategy-store.actions';
import {
  getCombinedDueDateOptions,
  getCompletionOptions,
  getCreatedDateOptions,
  getProjectOptions,
} from './epics-filters.helpers';

type FilterGroup = BaseFilterGroup & {
  id: string;
};

/**
 * Custom hook that returns an array of filter groups for the epic list view
 * Each filter group contains configuration for due date or project filtering
 *
 * The due date filter group allows filtering by epic due date state (All, Has Due Date, No Due Date)
 * The project filter group allows filtering by epic projects
 * The target end date filter group allows filtering by target end date (Next 90 Days, Last 90 Days)
 * The created date filter group allows filtering by created date (Last 90 Days)
 *
 * All groups support:
 * - Multiple selection
 * - Show all option
 * - Material icon
 * - Custom labels
 *
 * @returns {FilterGroup[]} Array of filter group configurations with current selected values and change handlers
 */
const useEpicFilterGroups = (): FilterGroup[] => {
  const i18n = useTranslation();
  // Use stable selectors to avoid infinite loops with getSnapshot
  const epicDueDateStateFilters = useStrategyStore((state) => state.filters.epic_due_date_state);
  const epicProjectFilters = useStrategyStore((state) => state.filters.epic_projects);
  const epicCompletionFilters = useStrategyStore((state) => state.filters.epic_completion);
  const epicTargetEndDateFilters = useStrategyStore((state) => state.filters.epic_target_end_date);
  const epicCreatedDateFilters = useStrategyStore((state) => state.filters.epic_created_date);
  const epics = useStrategyStore((state) => state.epics);

  // Memoize project options to prevent infinite loops
  const projectOptions = useMemo(() => getProjectOptions(epics as (Epic | StrategicEpic)[]), [epics]);

  const combinedDueDateOptions = useMemo(() => getCombinedDueDateOptions(i18n.t), [i18n.t]);
  const createdDateOptions = useMemo(() => getCreatedDateOptions(i18n.t), [i18n.t]);

  const completionOptions = useMemo(() => getCompletionOptions(i18n.t), [i18n.t]);

  // Combine selected values from both due date filters
  const combinedDueDateSelectedValues = useMemo(() => {
    return [...(epicDueDateStateFilters || []), ...(epicTargetEndDateFilters || [])];
  }, [epicDueDateStateFilters, epicTargetEndDateFilters]);

  // Handler that routes selections to the appropriate state setters
  const handleCombinedDueDateChange = useCallback((selectedValues: string[]) => {
    const dueDateStateValues = selectedValues.filter((v) => v === 'has_due_date' || v === 'no_due_date');
    const targetEndDateValues = selectedValues.filter((v) => v === 'next_90_days' || v === 'last_90_days');

    setEpicDueDateStateFilters(dueDateStateValues);
    setEpicTargetEndDateFilters(targetEndDateValues);
  }, []);

  return useMemo(() => {
    const filterGroups: FilterGroup[] = [
      {
        id: 'epic_completion',
        name: i18n.t('strategy.epics.completion'),
        icon: 'check_circle',
        options: completionOptions,
        selectedValues: epicCompletionFilters || [],
        onSelectionChange: setEpicCompletionFilters,
        showAll: false,
        showAllLabel: 'All',
      },
      {
        id: 'epic_due_date',
        name: i18n.t('strategy.epics.due_date'),
        icon: 'event',
        options: combinedDueDateOptions,
        selectedValues: combinedDueDateSelectedValues,
        onSelectionChange: handleCombinedDueDateChange,
        showAll: false,
        showAllLabel: 'All',
      },
      {
        id: 'epic_created_date',
        name: i18n.t('strategy.epics.created_date'),
        icon: 'add_circle',
        options: createdDateOptions,
        selectedValues: epicCreatedDateFilters || [],
        onSelectionChange: setEpicCreatedDateFilters,
        showAll: false,
        showAllLabel: 'All',
      },
      {
        id: 'epic_projects',
        name: i18n.t('strategy.epics.parent_project'),
        icon: 'folder_open',
        options: projectOptions,
        selectedValues: epicProjectFilters || [],
        onSelectionChange: setEpicProjectFilters,
        showAllLabel: `All ${i18n.t('common.teams')}`,
      },
    ];

    return filterGroups;
  }, [
    epicCompletionFilters,
    completionOptions,
    combinedDueDateOptions,
    combinedDueDateSelectedValues,
    handleCombinedDueDateChange,
    epicCreatedDateFilters,
    createdDateOptions,
    epicProjectFilters,
    projectOptions,
    i18n.t,
  ]);
};

export { useEpicFilterGroups };
